import styled, {css} from 'styled-components';

import {ellipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {compactCellBaseHeight} from '../cellBaseCommon';
import {CellAttachmentIcon} from '../content/cell/cellAttachmentIcon';
import {CellBadgeValue} from '../content/cell/cellBadgeValue';
import {CellDate} from '../content/cell/cellDate';
import {CellInternalIcon} from '../content/cell/cellInternalIcon';
import {CellStatus} from '../content/cell/cellStatus';
import {CellStatusIcon} from '../content/cell/cellStatusIcon';

const StyledCellBaseFirstThird = styled.div`
  grid-area: first;
  display: grid;
  grid-template-columns: minmax(17px, auto) 1fr;
  grid-template-areas: 'status recipient';
`;
const StyledCellBaseRecipient = styled.div`
  grid-area: recipient;
  ${ellipsis()};
`;

const StyledCellStatus = styled(CellStatus)`
  grid-area: status;
  margin-right: 7px;
`;

const StyledCellBaseMiddleThird = styled.div<{$hasTags: boolean}>`
  grid-area: second;
  ${ellipsis()};

  ${(p) =>
    !p.$hasTags
      ? css`
          display: flex;
          align-items: center;
        `
      : css`
          display: flex;
          align-items: center;
          grid-gap: 6px;
        `};

  /*
    This padding-margin trick lets the subject highlight background overflow on
    the left, with its rounded corners, instead of starting abruptly.

    See cellMiddle.tsx for the corresponding padding-left property.

    In the compact case, because the subject element is in a flexbox, we have to
    move the margin-left property to the first parent that is not inside a
    flexbox (here).
  */
  margin-left: -3px;
`;
const StyledCellBaseLastThird = styled.div`
  grid-area: third;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const StyledCellAttachmentRightIcon = styled(CellAttachmentIcon)``;

const StyledCellDate = styled(CellDate)`
  flex-shrink: 0;
  white-space: nowrap;
  transform: none;
  margin-left: 6px;
  text-align: right;
  display: flex;

  > div {
    margin-right: 0;
  }
`;

const StyledCellBadgeValue = styled(CellBadgeValue)`
  margin-left: 0px;
`;

const StyledCellInternalIcon = styled(CellInternalIcon)`
  grid-area: badge !important;
`;

const StyledCellStatusIcon = styled(CellStatusIcon)`
  padding-left: 6px;
  grid-area: status;
`;

const StyledContentWrapperDiv = styled.div`
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: 0;
  display: flex;
  align-items: center;
  height: ${compactCellBaseHeight};

  ${ellipsis()};
`;

const StyledSubjectWrapperDiv = styled.div<{$isSearchSummaryMatch?: boolean}>`
  height: ${compactCellBaseHeight};
  /* When searching, if we have a highlight on the summary: */
  /* Give the same shrink factor as the summary. See compactCellActivity.tsx. */
  flex-shrink: ${(p) => (p.$isSearchSummaryMatch ? 1 : 2)};
  min-width: ${(p) => (p.$isSearchSummaryMatch ? 'auto' : '5ch')};
  /* Split available space evenly between subject and summary. See compactCellActivity.tsx. */
  ${(p) =>
    p.$isSearchSummaryMatch &&
    `flex-basis: 50%;
     flex-grow: 1;`}
  /* Let the summary grow if the subject is smaller than half the available space. */
  max-width: max-content;
  ${ellipsis()};
  ${flexCenter};
`;

const StyledTagsWrapperDiv = styled.div<{$columns: number; $isInSearch?: boolean}>`
  display: grid;
  align-items: center;
  grid-template-columns: repeat(${(p) => p.$columns}, minmax(15px, auto));
  gap: 4px;
  max-width: ${(p) => (p.$isInSearch ? '20%' : '38%')};
  flex-shrink: 1;

  > div {
    margin-left: 0;
  }
`;

const StyledCellDraftWrapperDiv = styled.div`
  flex-shrink: 0;
`;

export const CompactCellComponents = {
  FirstThird: StyledCellBaseFirstThird,
  Recipient: StyledCellBaseRecipient,
  CellStatus: StyledCellStatus,
  MiddleThird: StyledCellBaseMiddleThird,
  LastThird: StyledCellBaseLastThird,
  AttachmentRightIcon: StyledCellAttachmentRightIcon,
  CellDate: StyledCellDate,
  BadgeValue: StyledCellBadgeValue,
  InternalIcon: StyledCellInternalIcon,
  StatusIcon: StyledCellStatusIcon,
  ContentWrapper: StyledContentWrapperDiv,
  SubjectWrapper: StyledSubjectWrapperDiv,
  TagsWrapper: StyledTagsWrapperDiv,
  CellDraftWrapper: StyledCellDraftWrapperDiv,
};
